import { useEffect, useState } from "react";
import { Loader2, Paperclip } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { PAY_METHODS } from "@/lib/conecta";

const upload = async (file) => {
  if (!file) return null;
  const fd = new FormData();
  fd.append("file", file, file.name);
  return (await api.post("/files", fd)).data.id;
};

const FilePick = ({ file, setFile, label, testid }) => (
  <label data-testid={`${testid}-label`} className="flex items-center gap-2 c-input cursor-pointer c-text-2">
    <Paperclip className="w-4 h-4" /> <span className="truncate">{file ? file.name : label}</span>
    <input data-testid={testid} type="file" accept="image/*,application/pdf" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
  </label>
);

const blank = (currency) => ({ date: new Date().toISOString().slice(0, 10), amount: "", currency: currency || "", method: "transferencia", invoice_issued: false, invoice_folio: "", notes: "" });

export const PaymentForm = ({ open, onOpenChange, currency, onSubmit }) => {
  const [f, setF] = useState(blank(currency));
  const [invoice, setInvoice] = useState(null);
  const [proof, setProof] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setF(blank(currency)); setInvoice(null); setProof(null); } }, [open, currency]);
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const [invoice_file_id, proof_file_id] = await Promise.all([upload(invoice), upload(proof)]);
      await onSubmit({ ...f, amount: Number(f.amount), currency: f.currency || null, date: f.date || null, invoice_folio: f.invoice_folio || null, notes: f.notes || null, invoice_file_id, proof_file_id });
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" data-testid="payment-form-sheet" className="max-h-[92vh] overflow-y-auto rounded-t-2xl pb-[max(1.5rem,env(safe-area-inset-bottom))]">
        <SheetHeader className="text-left">
          <SheetTitle className="font-heading">Registrar pago recibido</SheetTitle>
          <SheetDescription>Se permiten pagos parciales.</SheetDescription>
        </SheetHeader>
        <form onSubmit={submit} className="mt-4 space-y-3 max-w-md mx-auto">
          <div className="grid grid-cols-2 gap-2">
            <label className="space-y-1"><span className="text-xs c-text-2">Monto *</span><input data-testid="payment-amount-input" className="c-input font-mono" type="number" step="any" min="0" inputMode="decimal" required value={f.amount} onChange={set("amount")} /></label>
            <label className="space-y-1"><span className="text-xs c-text-2">Moneda</span>
              <select data-testid="payment-currency-select" className="c-input" value={f.currency} onChange={set("currency")}><option value="">Sin definir</option><option>MXN</option><option>USD</option><option>EUR</option></select>
            </label>
            <label className="space-y-1"><span className="text-xs c-text-2">Fecha</span><input data-testid="payment-date-input" className="c-input" type="date" value={f.date} onChange={set("date")} /></label>
            <label className="space-y-1"><span className="text-xs c-text-2">Forma de pago</span>
              <select data-testid="payment-method-select" className="c-input" value={f.method} onChange={set("method")}>{Object.entries(PAY_METHODS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
            </label>
          </div>
          <label className="flex items-center gap-2 c-input cursor-pointer"><input data-testid="payment-invoice-issued" type="checkbox" className="w-5 h-5 accent-[#bc9538]" checked={f.invoice_issued} onChange={set("invoice_issued")} /> <span className="text-sm">Se emitió factura</span></label>
          {f.invoice_issued && (
            <>
              <input data-testid="payment-invoice-folio-input" className="c-input" placeholder="Folio de la factura" value={f.invoice_folio} onChange={set("invoice_folio")} />
              <FilePick file={invoice} setFile={setInvoice} label="Factura (foto o PDF)" testid="payment-invoice-file-input" />
            </>
          )}
          <FilePick file={proof} setFile={setProof} label="Comprobante (opcional)" testid="payment-proof-file-input" />
          <textarea data-testid="payment-notes-input" className="c-input" rows={2} placeholder="Notas" value={f.notes} onChange={set("notes")} />
          <button data-testid="payment-submit-btn" disabled={busy} className="c-btn-primary w-full py-3.5 flex items-center justify-center gap-2">{busy && <Loader2 className="w-4 h-4 animate-spin" />} Registrar pago</button>
        </form>
      </SheetContent>
    </Sheet>
  );
};
